import React, { useEffect, useMemo } from 'react';
import { Box, Grid, TextField, Typography } from '@mui/material';
import { C } from '../../theme/adminTokens';


const FIELD_DEFS = [
  {
    key: 'globalMaxNormal',
    label: 'Global Max (Normal)',
    helperText: 'Must be at least 2.',
    isValid: (v) => Number.isFinite(v) && v >= 2,
  },
  {
    key: 'globalMaxAbsolute',
    label: 'Global Max (Absolute)',
    helperText: 'Must be greater than Global Max (Normal).',
    isValid: (v, values) => Number.isFinite(v) && v > Number(values.globalMaxNormal),
  },
  {
    key: 'maxPlayersPerRoom',
    label: 'Max Players Per Room',
    helperText: 'Must be between 2 and 100.',
    isValid: (v) => Number.isFinite(v) && v >= 2 && v <= 100,
  },
  {
    key: 'inactivityTimeoutSeconds',
    label: 'Inactivity Timeout (seconds)',
    helperText: 'Must be between 60 and 1800.',
    isValid: (v) => Number.isFinite(v) && v >= 60 && v <= 1800,
  },
  {
    key: 'maxSessionTimerSeconds',
    label: 'Max Session Timer (seconds)',
    helperText: 'Must be between 300 and 7200.',
    isValid: (v) => Number.isFinite(v) && v >= 300 && v <= 7200,
  },
];

/**
 * @param {object} props
 * @param {object} props.values 5 settings fields (globalMaxNormal, globalMaxAbsolute,
 *   maxPlayersPerRoom, inactivityTimeoutSeconds, maxSessionTimerSeconds)
 * @param {(next: object) => void} props.onChange reports the full updated values object up
 * @param {(isValid: boolean) => void} props.onValidityChange
 * @param {boolean} [props.disabled]
 */
const CcuSettingsForm = ({ values, onChange, onValidityChange, disabled = false }) => {
  const fieldErrors = useMemo(() => {
    const errs = {};
    FIELD_DEFS.forEach(({ key, isValid }) => {
      if (!isValid(Number(values[key]), values)) errs[key] = true;
    });
    return errs;
  }, [values]);

  const isValid = Object.keys(fieldErrors).length === 0;

  useEffect(() => {
    onValidityChange(isValid);
  }, [isValid, onValidityChange]);

  const handleFieldChange = (key) => (e) => {
    const raw = e.target.value;
    onChange({ ...values, [key]: raw === '' ? '' : Number(raw) });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: C.navy, mb: 1.5 }}>
          Global Settings
        </Typography>
        <Grid container spacing={2}>
          {FIELD_DEFS.map(({ key, label, helperText }) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={key}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label={label}
                value={values[key] ?? ''}
                onChange={handleFieldChange(key)}
                disabled={disabled}
                error={!!fieldErrors[key]}
                helperText={fieldErrors[key] ? helperText : ' '}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default CcuSettingsForm;
